'use client';

import { poiImagesModalAtom } from '@/atom/modal';
import { useSetAtom } from 'jotai';
import Link from 'next/link';
import ImageWithRetry from '../common/image-with-retry';
import { useEffect } from 'react';
import ImageWithFallback from '../common/image-with-fallback';
import { getPoiFallbackImageUrl } from '@/utils';
import './poi-detail-images.css';

export default function PoiDetailImages({
  poiImages,
  poiId,
  poiName,
  poiType,
  showFallback,
}: {
  poiImages: string[];
  poiId: string | null;
  poiName: string | null;
  poiType?: string | null;
  showFallback?: boolean;
}) {
  const setPoiImagesModal = useSetAtom(poiImagesModalAtom);

  if (poiImages.length === 0 && showFallback) {
    return (
      <div className='flex bg-gray-100 h-258pxr items-center justify-center'>
        <div
          id='poi-detail-images-fallback'
          className='flex flex-col items-center justify-center text-center opacity-0'
        >
          <div className='w-80pxr h-80pxr mb-16pxr relative'>
            <ImageWithFallback
              src=''
              alt='기본 이미지'
              width={80}
              height={80}
              className='w-full h-full object-cover rounded-8pxr opacity-60'
              fallbackSrc={getPoiFallbackImageUrl(poiType ?? 'attraction')}
            />
          </div>
          <p className='text-gray-500 body-14-r'>등록된 이미지가 없습니다</p>
        </div>
      </div>
    );
  } else if (poiImages.length === 0) {
    return null;
  }

  const renderImage = ({ src, index, className }: { src: string; index: number; className: string }) => {
    return (
      <ImageWithRetry
        src={src}
        alt={`poi-${index + 1}`}
        className={className}
        width={400}
        height={400}
        priority={true}
        unoptimized
      />
    );
  };

  return (
    <Link
      href={`/poi-images/${poiId}`}
      scroll={false}
      prefetch={false}
      className='flex bg-white h-258pxr'
      onClick={() => {
        setPoiImagesModal({
          visible: true,
          name: poiName ?? undefined,
          images: poiImages,
        });
      }}
    >
      <div className='h-full grow'>
        {renderImage({ src: poiImages[0], index: 0, className: 'object-cover w-full h-full' })}
      </div>
      {poiImages[1] && (
        <div className='w-128pxr h-full shrink-0 ml-2pxr'>
          {renderImage({ src: poiImages[1], index: 1, className: 'object-cover w-128pxr h-128pxr mb-2pxr' })}
          <div className='relative w-128pxr h-128pxr'>
            <div className='absolute top-0 left-0 w-full h-full flex items-center justify-center bg-gray-500/75'>
              <p className='text-center text-white body-14-r'>
                더 많은
                <br />
                사진 보기
              </p>
            </div>
            {poiImages[2] && renderImage({ src: poiImages[2], index: 2, className: 'object-cover w-full h-full' })}
          </div>
        </div>
      )}
    </Link>
  );
}
